import React, {useCallback} from 'react';
import {useField, useForm} from 'react-final-form';
import {IntlShape} from 'react-intl';
import cn from 'classnames';

import {FieldReact} from './Subcomponents';
import {FieldConfig, TFormatRule} from './types';

// eslint-disable-next-line no-restricted-imports
import './Field.scss';

/**
 * Validation rule with its error message translated (`schema.rules.x.map(formatRule(intl))`).
 * @param {IntlShape} intl - `useIntl()` of the component
 * @returns {TFormatRule} - Rule formatter
 */
export const formatRule = (intl: IntlShape): TFormatRule => (config) => ({
	errorMessage: intl.formatMessage(config?.errorMessage),
	rule: config?.rule,
});

const Field = (
	{
		children,
		comment,
		classNames,
		classNamesCntr = '',
		commentClass,
		errorClass,
		label,
		validation,
		component = 'input',
		customIcon,
		onClick,
		onChange,
		alwaysShowComment = false,
		requirednessLabel,
		showSubmitError = true,
		subscription = {touched: true, error: true, submitError: true},
		shouldBlur = true,
		onKeyPress,
		ref,
		...props
	}: FieldConfig,
) => {
	const {
		meta: {touched, error, submitError},
	} = useField(props.name, {subscription});

	const shouldShowValidationError = touched && !!error;
	const shouldShowSubmitError = showSubmitError && !!submitError;
	const isErrorShown = shouldShowValidationError || shouldShowSubmitError;
	const isCommentShown = !!comment && (alwaysShowComment || !isErrorShown);
	const form = useForm();
	const onChangeHandler = useCallback((e) => {
		if (component === 'select' && shouldBlur) {
			e.target.blur();
		}

		const currentValue = form.getFieldState(props?.name);

		if (currentValue?.value && Array.isArray(currentValue.value)) {
			if (e.target.checked) {
				form.change(props?.name, [...currentValue.value, props?.value]);
			} else {
				form.change(
					props?.name,
					currentValue.value.filter((val) => val !== props?.value),
				);
			}
		} else if (e.target.type === 'checkbox') {
			form.change(props?.name, e.target.checked);
		} else {
			form.change(props?.name, e.target.value);
		}

		if (typeof onChange === 'function') {
			onChange(e);
		}
	}, [onChange, shouldBlur, form, component]);

	return (
		<div className={cn('field', `field-${props.name}`, `field-${props.type}`, classNamesCntr)}>
			{label && !props.id ? (
				<label className={cn(classNames, {disabled: props.disabled})}>
					{label}
					{!!requirednessLabel && requirednessLabel}
					<Field.React
						classNames={classNames}
						component={component}
						isErrorShown={isErrorShown}
						validation={validation}
						customIcon={customIcon}
						onClick={onClick}
						onChange={onChangeHandler}
						onKeyPress={onKeyPress}
						{...props}
						ref={ref}
					>
						{children}
					</Field.React>
				</label>
			) : (
				<>
					<Field.React
						classNames={classNames}
						component={component}
						isErrorShown={isErrorShown}
						validation={validation}
						customIcon={customIcon}
						onClick={onClick}
						onChange={onChangeHandler}
						onKeyPress={onKeyPress}
						ref={ref}
						{...props}
					>
						{children}
					</Field.React>
					{/* as label wrapped around input generates two click events on one physical click,
					 * so this may lead to quite unexpected behaviour.
					 * specifically, variant with id and for attribute should be used for DatePicker input
					 */}
					{label && props.id && (
						<label className="label-sibling" htmlFor={props.id}>
							{label}
							{!!requirednessLabel && requirednessLabel}
						</label>
					)}
				</>
			)}

			{isCommentShown && <span className={cn('field-comment', commentClass)}>{comment}</span>}

			{isErrorShown && <span className={cn('field__error', errorClass)}>{error || submitError}</span>}
		</div>
	);
};

Field.React = FieldReact;

export default Field;
